import { useSyncExternalStore } from 'react';
import { preferenceStorage } from '../privacy/consent';

export type Theme = 'LIGHT' | 'DARK' | 'SYSTEM';
const KEY = 'lista.theme';

let current: Theme = 'LIGHT';
const listeners = new Set<() => void>();

/** UI preference applied to <html data-theme>; persisted locally and on the profile. */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'SYSTEM') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme.toLowerCase());
  current = theme;
  listeners.forEach((notify) => notify());
  try {
    preferenceStorage.setItem(KEY, theme);
  } catch {
    /* storage may be unavailable (private mode) */
  }
}

/** The saved preference on this device; light until the user picks something else. */
export function storedTheme(): Theme {
  try {
    const v = preferenceStorage.getItem(KEY);
    return v === 'DARK' || v === 'SYSTEM' ? v : 'LIGHT';
  } catch {
    return 'LIGHT';
  }
}

/** What the screen shows right now: SYSTEM resolves to the OS setting. */
export function effectiveTheme(theme: Theme): 'LIGHT' | 'DARK' {
  if (theme !== 'SYSTEM') return theme;
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    ? 'DARK'
    : 'LIGHT';
}

function subscribe(notify: () => void) {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

/** The applied theme preference, re-rendering when it changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, () => current);
}
